import { FC, useEffect, useState, useMemo } from "react";
import { Room } from "../../../models/Room";
import { My } from "../../../services/My";
import { User } from "../../../models/User";
import { Fire } from "../../../services/FireClient";
import { UserAvatar } from "../../components/UserAvatar";
import InviteLinkModal from "../../components/InviteLinkModal";
import UserPopOver from "../../components/UserPopover";
import AddPeopleModal from "./AddPeopleModal";
import CallButton from '../../components/CallButton';
import { RoomCall } from '../../../models/RoomCall';
import { Trans, t } from "@lingui/macro";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { useToast } from "../../../components/ui/use-toast";
import { Users, ChevronDown, ChevronUp, Crown } from "lucide-react";
import { Badge } from "../../../components/ui/badge";

export const PanelListMembers: FC<{ room: Room }> = ({ room }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [call] = useState<RoomCall>();
  const [members, setMembers] = useState<User[]>(room.users);
  const { toast } = useToast();

  useEffect(() => {
    setIsExpanded(false);
  }, [room.id]);

  // sync when room.users changes
  useEffect(() => {
    setMembers(room.users);
  }, [room.users]);

  // 방장을 맨 위로 정렬
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (a.id === room.creatorId) return -1;
      if (b.id === room.creatorId) return 1;
      return 0;
    });
  }, [members, room.creatorId]);

  function isCallOngoing() {
    return RoomCall.isActive(call);
  }

  const canKickMembers = !!Room.isCreator(room, My.user.id);

  async function handleKickMember(member: User) {
    try {
      setMembers((prev: User[]) => prev.filter((m: User) => m.id !== member.id));
      await Fire.removeRoomMember(room.id, member, My.user);
      toast({
        title: t`${member.name} has been kicked out`,
        duration: 1000,
      });
    } catch (e) {
      setMembers(room.users); // rollback
      toast({
        title: t`Failed to kick out`,
        duration: 1500,
      });
    }
  }

  return (
    <div className="px-4 w-full mb-1 bg-gray-50 rounded-lg">
      {/* 헤더 */}
      <div className="flex items-center justify-between py-2 group">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-sm font-semibold text-gray-900 transition-colors"
          >
            <Users className="w-4 h-4 text-gray-500" />
            <span>{room.users.length}</span>
            <span className="text-gray-400 ml-0.5 transition-opacity">
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </span>
          </button>

          {!Room.isArchivedOneToOne(room) && (
            <CallButton room={room} isOngoing={isCallOngoing()} call={call} />
          )}
        </div>

        {!room.isOneToOne && (
          <div className="flex items-center gap-1">
            {Room.isCreator(room, My.user.id) && <AddPeopleModal room={room} />}
            <InviteLinkModal room={room} />
          </div>
        )}
      </div>

      {/* 바디 */}
      <div className="pb-2">
        {/* 접힌 상태: 그리드로 아바타만 표시 */}
        {!isExpanded && (
          <div className="flex flex-wrap gap-2 animate-in slide-in-from-top-1 duration-200">
            {sortedMembers.slice(0, 10).filter(u => !!u).map((u) => (
              <UserPopOver
                user={u}
                key={u.id}
                room={room}
                isCreator={u.id === room.creatorId}
                canKick={canKickMembers}
                onKick={handleKickMember}
                content={
                  <div className="relative w-9 h-9 cursor-pointer hover:opacity-80 transition-opacity">
                    <UserAvatar
                      src={u.photoUrl}
                      name={u.name}
                      className="w-full h-full hover:ring-2 hover:ring-orange-200 transition-all"
                    />
                    {u.id === room.creatorId && (
                      <div className="absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-0.5 z-10 border border-white">
                        <Crown className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>
                }
              />
            ))}
            {sortedMembers.length > 10 && (
              <button
                onClick={() => setIsExpanded(true)}
                className="w-9 h-9 rounded-[var(--radius-avatar)] bg-gray-100 flex items-center justify-center text-xs text-gray-500 hover:bg-gray-200 transition-colors"
              >
                +{sortedMembers.length - 10}
              </button>
            )}
          </div>
        )}

        {/* 펼친 상태: 리스트로 상세 정보 표시 */}
        {isExpanded && (
          <div className="flex flex-col -mx-2 animate-in slide-in-from-top-1 duration-200">
            {sortedMembers.filter(u => !!u).map((u) => (
              <UserPopOver
                user={u}
                key={u.id}
                room={room}
                isCreator={u.id === room.creatorId}
                canKick={canKickMembers}
                onKick={handleKickMember}
                content={
                  <div className="flex items-center gap-3 py-2.5 px-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                    <div className="relative w-10 h-10 flex-shrink-0">
                      <UserAvatar
                        src={u.photoUrl}
                        name={u.name}
                        className="w-full h-full"
                      />
                      {u.id === room.creatorId && (
                        <div className="absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-0.5 z-10 border border-white">
                          <Crown className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate text-gray-900">{u.name}</h3>
                        {u.id === room.creatorId && (
                          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] px-1.5 py-0 gap-0.5">
                            <Crown className="w-2.5 h-2.5" />
                            <Trans>Owner</Trans>
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </div>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
